"use client";

import { useState, useCallback, useRef } from "react";
import Link from "next/link";
import { Fingerprint, Wallet, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSession } from "next-auth/react";
import { usePasskey } from "@/contexts/PasskeyContext";
import { getTxExplorerUrl, getContractExplorerUrl } from "@/lib/hoops-contracts";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Deployment step tracking
// ---------------------------------------------------------------------------

type DeployStep = "idle" | "passkey" | "deploying" | "initializing" | "done" | "error";

const STEP_LABELS: Record<DeployStep, string> = {
  idle: "",
  passkey: "Creating passkey...",
  deploying: "Deploying contract...",
  initializing: "Initializing...",
  done: "Done!",
  error: "",
};

// Progress indicator shown while deployment is in flight or complete.
function DeployProgress({
  step,
  contractId,
  deployHash,
}: {
  step: DeployStep;
  contractId: string | null;
  deployHash: string | null;
}) {
  const steps: { id: DeployStep; label: string }[] = [
    { id: "passkey", label: "Passkey" },
    { id: "deploying", label: "Deploy" },
    { id: "initializing", label: "Init" },
    { id: "done", label: "Ready" },
  ];

  const ORDER: DeployStep[] = ["passkey", "deploying", "initializing", "done"];
  const currentIdx = ORDER.indexOf(step);

  function isCompleted(s: DeployStep) {
    return currentIdx > ORDER.indexOf(s);
  }
  function isActive(s: DeployStep) {
    return step === s;
  }

  return (
    <div className="w-full mt-4">
      {/* Step track */}
      <div className="flex items-center justify-between mb-3" role="list" aria-label="Deployment progress">
        {steps.map((s, idx) => {
          const completed = isCompleted(s.id);
          const active = isActive(s.id);
          const isDone = s.id === "done" && step === "done";

          return (
            <div key={s.id} className="flex items-center flex-1" role="listitem">
              {/* Node */}
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <div
                  className={cn(
                    "h-7 w-7 rounded-full flex items-center justify-center transition-all duration-300",
                    completed || isDone
                      ? "bg-[var(--accent)]"
                      : active
                        ? "bg-[var(--accent-dim)] ring-2 ring-[var(--accent)]"
                        : "bg-[var(--surface-2)]",
                  )}
                  aria-hidden="true"
                >
                  {completed || isDone ? (
                    <CheckCircle2 className="h-4 w-4" style={{ color: "var(--bg)" }} />
                  ) : active ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: "var(--accent)" }} />
                  ) : (
                    <span className="text-[10px] font-medium" style={{ color: "var(--text-secondary)" }}>
                      {idx + 1}
                    </span>
                  )}
                </div>
                <span
                  className="text-[10px] font-medium"
                  style={{
                    color: completed || isDone || active ? "var(--text-primary)" : "var(--text-secondary)",
                  }}
                >
                  {s.label}
                </span>
              </div>

              {/* Connector line between nodes */}
              {idx < steps.length - 1 && (
                <div
                  className="flex-1 h-px mx-2 transition-all duration-500"
                  style={{
                    background: completed
                      ? "var(--accent)"
                      : "var(--border-subtle)",
                  }}
                  aria-hidden="true"
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Status message */}
      {step !== "idle" && step !== "done" && (
        <p
          className="text-xs text-center mt-1"
          style={{ color: "var(--text-secondary)" }}
          aria-live="polite"
          role="status"
        >
          {STEP_LABELS[step]}
        </p>
      )}

      {/* Success: show contract address */}
      {step === "done" && contractId && (
        <div
          className="mt-3 rounded-[var(--r)] px-3 py-2.5 flex flex-col gap-1"
          style={{
            background: "rgba(62, 207, 126, 0.08)",
            border: "1px solid rgba(62, 207, 126, 0.25)",
          }}
          aria-live="polite"
        >
          <p className="text-[11px] font-medium" style={{ color: "var(--accent)" }}>
            Smart account deployed
          </p>
          <a
            href={getContractExplorerUrl(contractId)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-mono hover:opacity-80 transition-opacity"
            style={{ color: "var(--text-secondary)" }}
            aria-label={`View contract ${contractId} on Stellar Explorer`}
          >
            {contractId.slice(0, 14)}&hellip;{contractId.slice(-6)}
            <ExternalLink className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
          </a>
          {deployHash && (
            <a
              href={getTxExplorerUrl(deployHash)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] hover:opacity-80 transition-opacity"
              style={{ color: "var(--text-secondary)" }}
              aria-label="View deploy transaction on Stellar Explorer"
            >
              View deploy TX
              <ExternalLink className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Passkey user variant: deploy smart account inline
// ---------------------------------------------------------------------------

function PasskeyDeployPrompt() {
  const { data: session } = useSession();
  const { deployWithExistingPasskey, isLoading } = usePasskey();

  const [deployStep, setDeployStep] = useState<DeployStep>("idle");
  const [contractId, setContractId] = useState<string | null>(null);
  const [deployHash, setDeployHash] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const busyRef = useRef(false);

  const handleDeploy = useCallback(async () => {
    if (busyRef.current || isLoading) return;
    busyRef.current = true;
    setErrorMessage(null);

    const user = session?.user;
    let credId = user?.credentialId;
    let p256Hex = user?.p256PublicKeyHex;

    try {
      // If session doesn't have credential data, fetch from profile API
      if (!credId || !p256Hex) {
        setDeployStep("passkey");
        try {
          const profileRes = await fetch("/api/auth/profile/one");
          if (profileRes.ok) {
            const profile = await profileRes.json() as { passkeys?: { credId: string }[]; passkeyWalletKeyId?: string };
            if (profile.passkeys?.[0]?.credId) credId = profile.passkeys[0].credId;
            if (profile.passkeyWalletKeyId) p256Hex = profile.passkeyWalletKeyId;
          }
        } catch {
          // Profile fetch failed — will fall through to error below
        }
      }

      if (!credId || !p256Hex) {
        setErrorMessage("Passkey data not found. Please log out and sign in again with your passkey.");
        setDeployStep("error");
        busyRef.current = false;
        return;
      }

      setDeployStep("deploying");
      const result = await deployWithExistingPasskey(credId, p256Hex);

      if (!result) {
        setErrorMessage("Wallet creation failed. Please try again.");
        setDeployStep("error");
        return;
      }

      // The kit does deploy + init atomically; we animate through the steps
      // with a brief pause to give the user visual feedback.
      setDeployStep("deploying");
      await new Promise((res) => setTimeout(res, 600));

      setDeployStep("initializing");
      await new Promise((res) => setTimeout(res, 400));

      setDeployStep("done");
      setContractId(result.contractId);
      setDeployHash(result.deployHash);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Deployment failed. Please try again.";
      setErrorMessage(msg);
      setDeployStep("error");
    } finally {
      busyRef.current = false;
    }
  }, [deployWithExistingPasskey, isLoading, session]);

  const isDeploying =
    deployStep === "passkey" ||
    deployStep === "deploying" ||
    deployStep === "initializing";
  const isDone = deployStep === "done";

  return (
    <section aria-label="Deploy your smart account">
      <div
        className="rounded-[var(--r)] px-6 py-10 flex flex-col items-center justify-center text-center anim-fadeSlideInUp-500ms"
        style={{
          background: "var(--surface)",
          border: "2px dashed var(--border-default)",
        }}
      >
        {/* Icon */}
        <div
          className={cn(
            "h-14 w-14 rounded-full flex items-center justify-center mb-5 transition-colors duration-300",
          )}
          style={{ background: isDone ? "rgba(62, 207, 126, 0.12)" : "var(--accent-dim)" }}
          aria-hidden="true"
        >
          {isDone ? (
            <CheckCircle2 className="h-7 w-7" style={{ color: "var(--accent)" }} strokeWidth={1.5} />
          ) : (
            <Wallet className="h-7 w-7" style={{ color: "var(--accent)" }} strokeWidth={1.5} />
          )}
        </div>

        <h2
          className="text-base font-medium mb-2"
          style={{ color: "var(--text-primary)" }}
        >
          {isDone ? "Smart account deployed" : "Deploy your smart account"}
        </h2>

        {!isDone && (
          <p
            className="text-sm max-w-[360px] mb-6 leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            Your passkey is ready. One tap deploys your on-chain smart account
            — no seed phrase, no custody.
          </p>
        )}

        {/* Error message */}
        {deployStep === "error" && errorMessage && (
          <p
            className="text-sm mb-4 max-w-[360px] leading-relaxed"
            style={{ color: "#e05252" }}
            role="alert"
            aria-live="assertive"
          >
            {errorMessage}
          </p>
        )}

        {/* Deploy button — hidden once done */}
        {!isDone && (
          <Button
            variant="default"
            size="sm"
            className="px-6"
            disabled={isDeploying}
            onClick={() => void handleDeploy()}
            aria-label="Deploy your smart account using your passkey"
          >
            {isDeploying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {STEP_LABELS[deployStep]}
              </>
            ) : (
              <>
                <Fingerprint className="h-4 w-4" aria-hidden="true" />
                Deploy Smart Account
              </>
            )}
          </Button>
        )}

        {/* Progress steps — visible while deploying or after completion */}
        {deployStep !== "idle" && deployStep !== "error" && (
          <div className="w-full max-w-sm mt-2">
            <DeployProgress
              step={deployStep}
              contractId={contractId}
              deployHash={deployHash}
            />
          </div>
        )}

        {/* Continue CTA once done */}
        {isDone && (
          <p
            className="mt-5 text-sm"
            style={{ color: "var(--text-secondary)" }}
          >
            Fund your account to start earning.
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Magic-link user variant: prompt to add passkey
// ---------------------------------------------------------------------------

function MagicLinkPrompt() {
  return (
    <section aria-label="Get started with a passkey">
      <div
        className="rounded-[var(--r)] px-6 py-10 flex flex-col items-center justify-center text-center anim-fadeSlideInUp-500ms"
        style={{
          background: "var(--surface)",
          border: "2px dashed var(--border-default)",
        }}
      >
        <div
          className="h-14 w-14 rounded-full flex items-center justify-center mb-5"
          style={{ background: "var(--accent-dim)" }}
          aria-hidden="true"
        >
          <Fingerprint
            className="h-7 w-7"
            style={{ color: "var(--accent)" }}
            strokeWidth={1.5}
          />
        </div>

        <h2
          className="text-base font-medium mb-2"
          style={{ color: "var(--text-primary)" }}
        >
          Start earning on Stellar
        </h2>

        <p
          className="text-sm max-w-[320px] mb-6 leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          Add a passkey to create your smart account. Your fingerprint or device
          PIN secures all deposits — no seed phrase required.
        </p>

        <Button asChild variant="default" size="sm" className="px-6">
          <Link href="/auth" aria-label="Set up your passkey to get started">
            Add a Passkey
          </Link>
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Public export — routes to the correct variant based on session
// ---------------------------------------------------------------------------

export function NoWalletPrompt() {
  const { data: session } = useSession();
  const hasPasskey = session?.user.hasPasskey === true;

  if (hasPasskey) {
    return <PasskeyDeployPrompt />;
  }

  return <MagicLinkPrompt />;
}
